import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getStockDetails,
  getStockLedger,
} from "../../services/api/stocks/stockService";

const StockDetails = () => {
  const { id } = useParams();

  const [stock, setStock] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    fetchStockDetails();
  }, []);

  const fetchStockDetails = async () => {
    try {
      const stockRes = await getStockDetails(id);
      const ledgerRes = await getStockLedger(id);

      setStock(stockRes.data.data);
      setLedger(ledgerRes.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const filteredLedger = useMemo(() => {
    return ledger.filter((item) => {
      const matchesSearch =
        item.reference_type
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        item.reference_id?.toString().includes(search);

      const matchesType =
        filterType === "" || item.type === filterType;

      return matchesSearch && matchesType;
    });
  }, [ledger, search, filterType]);

  let runningBalance = 0;

  const ledgerWithBalance = filteredLedger.map((item) => {
    if (item.type === "IN") {
      runningBalance += Number(item.quantity);
    } else {
      runningBalance -= Number(item.quantity);
    }

    return {
      ...item,
      balance: runningBalance,
    };
  });

  const totalIn = ledger
    .filter((item) => item.type === "IN")
    .reduce((sum, item) => sum + Number(item.quantity), 0);

  const totalOut = ledger
    .filter((item) => item.type === "OUT")
    .reduce((sum, item) => sum + Number(item.quantity), 0);

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen space-y-6">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <h1 className="text-2xl font-bold text-gray-800">
          Stock Ledger Details
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Track stock movement and balance
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <p className="text-sm text-gray-500">Item Name</p>
          <p className="font-semibold mt-1">
            {stock?.boq_item?.item_name}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <p className="text-sm text-gray-500">Available Qty</p>
          <p className="font-bold text-blue-600 text-xl">
            {stock?.available_qty}
          </p>
        </div>

        <div className="bg-green-50 rounded-2xl border p-5">
          <p className="text-sm text-green-700">Total IN</p>
          <p className="font-bold text-xl">{totalIn}</p>
        </div>

        <div className="bg-red-50 rounded-2xl border p-5">
          <p className="text-sm text-red-700">Total OUT</p>
          <p className="font-bold text-xl">{totalOut}</p>
        </div>

      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 flex flex-col md:flex-row gap-3">

        <input
          type="text"
          placeholder="Search reference..."
          className="border rounded-lg px-4 py-2 w-full md:w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="border rounded-lg px-4 py-2 w-full md:w-48"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="IN">IN</option>
          <option value="OUT">OUT</option>
        </select>

      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Type</th>
                <th className="p-4 text-left">Qty</th>
                <th className="p-4 text-left">Reference</th>
                <th className="p-4 text-left">Balance</th>
              </tr>
            </thead>

            <tbody>
              {ledgerWithBalance.length > 0 ? (
                ledgerWithBalance.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="p-4">{formatDate(item.created_at)}</td>

                    <td className="p-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.type === "IN"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {item.type}
                      </span>
                    </td>

                    <td className="p-4">{item.quantity}</td>

                    <td className="p-4">
                      {item.reference_type} #{item.reference_id}
                    </td>

                    <td className="p-4 font-semibold text-blue-600">
                      {item.balance}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="p-5 text-center text-gray-500"
                  >
                    No ledger found
                  </td>
                </tr>
              )}
            </tbody>

          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {ledgerWithBalance.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-2xl border shadow-sm p-4"
          >
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {formatDate(item.created_at)}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-xs ${
                  item.type === "IN"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {item.type}
              </span>
            </div>

            <div className="mt-3 space-y-2 text-sm">
              <p>Qty: {item.quantity}</p>
              <p>
                Ref: {item.reference_type} #{item.reference_id}
              </p>
              <p className="font-semibold text-blue-600">
                Balance: {item.balance}
              </p>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default StockDetails;